/**
 * Query Cache - Application-level caching layer
 * AdminMunicipal 6.0
 *
 * SCALE-HIGH-001 Fix: Prevents redundant API calls by caching frequently accessed data
 *
 * Features:
 * - In-memory caching with configurable TTL (time-to-live)
 * - Pattern-based invalidation (e.g., invalidate all keys starting with 'propuestas:')
 * - Graceful cache misses
 * - Organization-scoped cache keys
 */

import { logger } from '../logger';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class QueryCache {
  private cache = new Map<string, CacheEntry<unknown>>();

  /**
   * Get cached data by key
   * Returns null if key not found or TTL expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      logger.log('[QueryCache] Cache miss:', key);
      return null;
    }

    const now = Date.now();
    const age = now - entry.timestamp;

    if (age > entry.ttl) {
      logger.log('[QueryCache] Cache expired:', key, `(age: ${age}ms, ttl: ${entry.ttl}ms)`);
      this.cache.delete(key);
      return null;
    }

    logger.log('[QueryCache] Cache hit:', key, `(age: ${age}ms, ttl: ${entry.ttl}ms)`);
    return entry.data as T;
  }

  /**
   * Set cached data with TTL
   * @param key - Unique cache key
   * @param data - Data to cache
   * @param ttl - Time to live in milliseconds (default: 60000 = 1 minute)
   */
  set<T>(key: string, data: T, ttl = 60000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
    logger.log('[QueryCache] Cached:', key, `(ttl: ${ttl}ms, size: ${this.cache.size})`);
  }

  /**
   * Invalidate cache entries matching a prefix
   * Useful for invalidating all queries related to a specific entity type
   *
   * Examples:
   * - invalidate('propuestas:') - clears all propuestas queries
   * - invalidate('propuestas:org123') - clears all propuestas for organization 123
   * - invalidate('') - clears entire cache
   */
  invalidate(prefix: string): void {
    let deletedCount = 0;

    if (prefix === '') {
      // Clear entire cache
      deletedCount = this.cache.size;
      this.cache.clear();
      logger.log('[QueryCache] Cleared entire cache:', deletedCount, 'entries');
      return;
    }

    // Delete entries matching prefix
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
        deletedCount++;
      }
    }

    logger.log('[QueryCache] Invalidated:', deletedCount, 'entries with prefix:', prefix);
  }

  /**
   * Invalidate a specific cache key
   */
  invalidateKey(key: string): void {
    const deleted = this.cache.delete(key);
    if (deleted) {
      logger.log('[QueryCache] Invalidated key:', key);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getStats() {
    const now = Date.now();
    let validCount = 0;
    let expiredCount = 0;

    for (const entry of this.cache.values()) {
      const age = now - entry.timestamp;
      if (age > entry.ttl) {
        expiredCount++;
      } else {
        validCount++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries: validCount,
      expiredEntries: expiredCount,
    };
  }

  /**
   * Clean up expired entries (optional maintenance)
   * This is called automatically on cache operations, but can be called manually
   */
  cleanup(): void {
    const now = Date.now();
    let deletedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age > entry.ttl) {
        this.cache.delete(key);
        deletedCount++;
      }
    }

    if (deletedCount > 0) {
      logger.log('[QueryCache] Cleanup: removed', deletedCount, 'expired entries');
    }
  }
}

// Singleton instance
export const queryCache = new QueryCache();

/**
 * Helper to generate cache keys from filters
 * Serializes filter object into a stable string representation
 */
export function generateCacheKey(
  prefix: string,
  organizationId: string | undefined,
  filters?: Record<string, unknown>
): string {
  const parts = [prefix];

  // Add organization ID (critical for multi-tenancy)
  if (organizationId) {
    parts.push(`org:${organizationId}`);
  } else {
    parts.push('org:unknown');
  }

  // Add filters in stable order
  if (filters) {
    const filterKeys = Object.keys(filters).sort();
    for (const key of filterKeys) {
      const value = filters[key];
      if (value !== undefined && value !== null) {
        parts.push(`${key}:${JSON.stringify(value)}`);
      }
    }
  }

  return parts.join('|');
}

/**
 * Cache configuration constants
 */
export const CacheTTL = {
  /** Very short TTL for frequently changing data (30 seconds) */
  SHORT: 30 * 1000,

  /** Medium TTL for semi-static data (1 minute) - DEFAULT */
  MEDIUM: 60 * 1000,

  /** Long TTL for rarely changing data like categories (5 minutes) */
  LONG: 5 * 60 * 1000,

  /** Very long TTL for static data like municipalities (30 minutes) */
  VERY_LONG: 30 * 60 * 1000,
} as const;
